// Port of MarkdownReportRenderer.cs:247-405 (AppendMarketAnalysis).
//
// Derives the per-market "Analysis & Recommendations" callout list from
// the raw data — same rules and thresholds as the CLI. Pure functions;
// the consuming component renders the returned bullets as a list.
//
// Returns: { callouts: string[], recommendations: string[] }

import { clusterCtrExcludingVideo, dailyMeanCtr } from './aggregations';
import { NON_CTR_TYPES, ZIP_SCOPED_MARKET, CAMPAIGN_ONLY_MARKETS, SEGMENT_MARKETS, SEGMENT_PATTERN } from './report-config';
import { clean } from './title-resolver';
const fmt = {
  int: n => Number(n || 0).toLocaleString('en-US'),
  dec1: n => Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }),
  dec2: n => Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }),
  dec3: n => Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3
  }),
  pct: n => `${Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}%`,
  money0: n => `$${Number(n || 0).toLocaleString('en-US', {
    maximumFractionDigits: 0
  })}`
};

// allMarketData: { [marketName]: { rows, place, topProducts, topCategories } }
export function deriveAnalysis(market, marketEntry, allMarketData) {
  const callouts = [];
  const recs = [];
  const rows = marketEntry.rows || [];
  const place = marketEntry.place;
  const topProducts = marketEntry.topProducts || [];
  const topCategories = marketEntry.topCategories || [];

  // Cluster baselines: exclude zip-scoped market and campaign-only markets.
  const comparableRows = [];
  for (const [m, d] of Object.entries(allMarketData || {})) {
    if (m === ZIP_SCOPED_MARKET) continue;
    if (CAMPAIGN_ONLY_MARKETS.has(m)) continue;
    for (const r of d.rows || []) comparableRows.push(r);
  }
  const clusterCtr = comparableRows.length > 0 ? clusterCtrExcludingVideo(comparableRows) : 0;
  const clusterViews = Object.values(allMarketData || {}).reduce((s, d) => s + (d.place && d.place.views || 0), 0);
  const clusterSessions = Object.values(allMarketData || {}).reduce((s, d) => s + (d.place && d.place.sessions || 0), 0);
  const clusterPps = clusterSessions > 0 ? clusterViews / clusterSessions : 0;

  // 1. CTR vs cluster
  if (rows.length > 0) {
    const mktCtr = clusterCtrExcludingVideo(rows);
    if (mktCtr > 0 && clusterCtr > 0) {
      const diff = mktCtr - clusterCtr;
      const sign = diff >= 0 ? 'above' : 'below';
      callouts.push(`Click-driven CTR of **${fmt.pct(mktCtr).replace('%', '%')}** — ${fmt.dec2(Math.abs(diff))} pp ${sign} the cluster average of ${fmt.pct(clusterCtr)}.`);
    }

    // 2. Top-CTR channel
    const grouped = new Map();
    for (const r of rows) {
      if (NON_CTR_TYPES.has(r.type)) continue;
      if (!grouped.has(r.type)) grouped.set(r.type, []);
      grouped.get(r.type).push(r);
    }
    const channelStats = [];
    for (const [type, group] of grouped) {
      const ctr = dailyMeanCtr(group);
      const clicks = group.reduce((s, x) => s + (x.clicks || 0), 0);
      if (clicks > 0 && ctr > 0) channelStats.push({
        type,
        ctr,
        clicks
      });
    }
    channelStats.sort((a, b) => b.ctr - a.ctr);
    const top = channelStats[0];
    if (top) {
      callouts.push(`Highest-CTR channel: **${top.type}** at ${fmt.pct(top.ctr)} (${fmt.int(top.clicks)} clicks).`);
    }
  }

  // 3. Engagement quality vs cluster
  if (place && place.sessions > 0) {
    const mktPps = place.views / place.sessions;
    let quality;
    if (clusterPps > 0) {
      const diff = mktPps - clusterPps;
      const sign = diff >= 0 ? 'above' : 'below';
      quality = `${fmt.dec2(mktPps)} pages/session, ${Math.round(place.avgTimeSpan || 0)}s avg — ${fmt.dec2(Math.abs(diff))} pp ${sign} cluster avg of ${fmt.dec2(clusterPps)} pps.`;
    } else {
      quality = `${fmt.dec2(mktPps)} pages/session, ${Math.round(place.avgTimeSpan || 0)}s avg.`;
    }
    callouts.push(`Engagement quality: ${quality}`);
  }

  // 4. Top product
  if (topProducts.length > 0) {
    const t = topProducts[0];
    let convPart = '';
    if (t.conversions > 0) {
      convPart = `, ${fmt.int(t.conversions)} conv`;
      if (t.conversionsPriceValue > 0) {
        convPart += ` → ${fmt.money0(t.conversionsPriceValue)}`;
      }
    }
    callouts.push(`Most-viewed product: **${clean(t.title) || t.productId}** (${fmt.int(t.views)} views${convPart}).`);
  }

  // 5. Top category by conversion value, then by views.
  if (topCategories.length > 0) {
    const byConv = topCategories.filter(c => c.conversionsPriceValue > 0).sort((a, b) => b.conversionsPriceValue - a.conversionsPriceValue)[0];
    if (byConv) {
      callouts.push(`Top category by conversion value: **${byConv.category}** (${byConv.products} products → ${fmt.money0(byConv.conversionsPriceValue)} conv value).`);
    }
    const byViews = topCategories[0];
    if (!byConv || byConv.category !== byViews.category) {
      callouts.push(`Top category by views: **${byViews.category}** (${byViews.products} products, ${fmt.int(byViews.views)} views).`);
    }
  }

  // 6. Conversion rate vs furniture-retail benchmark
  if (place && place.views > 0) {
    const rate = place.conversions / place.views * 100;
    let commentary;
    if (rate < 0.05) commentary = 'well below the 1–2% furniture-retail benchmark — friction likely in wishlist / account-creation.';else if (rate < 0.5) commentary = 'below the 1–2% furniture-retail benchmark — review the wishlist / account-creation step.';else if (rate < 1.0) commentary = 'approaching the lower bound of the 1–2% furniture-retail benchmark.';else if (rate < 2.0) commentary = 'within the 1–2% furniture-retail benchmark range.';else commentary = 'above the 1–2% furniture-retail benchmark.';
    callouts.push(`Conversion rate: ${fmt.int(place.conversions)} on ${fmt.int(place.views)} views = ${fmt.dec3(rate)}% — ${commentary}`);
  }

  // 7. Recommendations
  if (rows.length > 0 && clusterCtr > 0) {
    const mktCtr = clusterCtrExcludingVideo(rows);
    if (mktCtr > 0 && mktCtr < clusterCtr - 0.5) {
      const topCtrMarkets = [];
      for (const [m, d] of Object.entries(allMarketData || {})) {
        if (m === market) continue;
        if (m === ZIP_SCOPED_MARKET) continue;
        if (CAMPAIGN_ONLY_MARKETS.has(m)) continue;
        const ctr = clusterCtrExcludingVideo(d.rows || []);
        if (ctr > 0) topCtrMarkets.push({
          market: m,
          ctr
        });
      }
      topCtrMarkets.sort((a, b) => b.ctr - a.ctr);
      const topMkt = topCtrMarkets[0];
      if (topMkt) {
        recs.push(`CTR trails the cluster — borrow the creative + landing-page combination from **${topMkt.market}** (${fmt.pct(topMkt.ctr)} CTR) and A/B test against ${market}'s current mix.`);
      }
    }
  }
  if (place && place.sessions > 0 && place.avgTimeSpan < 15) {
    recs.push("Avg session is under 15 seconds — visitors arrive but don't engage. Worth a creative-to-landing-page audit for this market specifically.");
  }
  if (topProducts.length > 0 && place && place.conversions === 0 && place.views > 100) {
    recs.push(`Strong impression + view delivery but no wishlist conversions — surface a closing-CTA / follow-up flow for ${clean(topProducts[0].title) || topProducts[0].productId} specifically.`);
  }
  if (SEGMENT_MARKETS.has(market)) {
    const segRows = rows.filter(r => r.type === 'Social' && SEGMENT_PATTERN.test(r.campaign));
    const nonSegRows = rows.filter(r => r.type === 'Social' && !SEGMENT_PATTERN.test(r.campaign));
    if (segRows.length > 0 && nonSegRows.length > 0) {
      const segCtr = dailyMeanCtr(segRows);
      const nonSegCtr = dailyMeanCtr(nonSegRows);
      if (segCtr < nonSegCtr - 0.5) {
        recs.push(`C1/C2/C3 segment campaigns are trailing broad Social by ${fmt.dec2(nonSegCtr - segCtr)} pp (${fmt.pct(segCtr)} vs ${fmt.pct(nonSegCtr)}) — consider widening the lookalike windows or refreshing creative for the tiers in ${market}.`);
      }
    }
  }
  return {
    callouts,
    recommendations: recs
  };
}